import { useMemo, useState, type ReactElement } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from '../lib/auth/session';
import { createQueryClient } from '../lib/api/queries';
import type { ClientSurface } from '../lib/api/client';
import { Shell, type NavSection } from './Shell';
import { CustomerSignIn, StaffSignIn } from './SignIn';

/**
 * The environment banner is driven by build configuration, never guessed: a deployment that does
 * not set `VITE_ENVIRONMENT` is production and shows no banner.
 */
function environmentName(): string | null {
  const raw = import.meta.env.VITE_ENVIRONMENT as string | undefined;
  if (!raw || raw === 'production') return null;
  return raw;
}

function PortalGate({
  surface,
  portalName,
  sections,
}: {
  surface: ClientSurface;
  portalName: string;
  sections: NavSection[];
}): ReactElement {
  const session = useSession();
  if (session.status !== 'authenticated') {
    return surface === 'portal' ? <CustomerSignIn /> : <StaffSignIn />;
  }
  return <Shell portalName={portalName} sections={sections} environment={environmentName()} />;
}

/**
 * One portal: its surface header value, its own session marker, its own query client.
 *
 * The query client is created per portal so a `401` clears exactly this portal's marker, and so
 * switching portals never serves one portal's cache to another.
 */
export function Portal({
  surface,
  portalName,
  sections,
}: {
  surface: ClientSurface;
  portalName: string;
  sections: NavSection[];
}): ReactElement {
  return (
    <SessionProvider surface={surface}>
      <PortalQueryScope>
        <PortalGate surface={surface} portalName={portalName} sections={sections} />
      </PortalQueryScope>
    </SessionProvider>
  );
}

function PortalQueryScope({ children }: { children: ReactElement }): ReactElement {
  const session = useSession();
  const [client] = useState(() => createQueryClient(() => session.markUnauthenticated()));
  const value = useMemo(() => client, [client]);
  return <QueryClientProvider client={value}>{children}</QueryClientProvider>;
}
